import _ from 'lodash'
import { Dispatch } from 'redux'
//******************  ******************//
import { AssetTypesResponse } from "@cmodules/asset/assets/data/types"
import { _getParentPage } from "@cmodules/checklist/utility/template-builder-utilities"
import {
  CategoryVM,
  GlobalItemType,
  ItemVM,
  PublicLibraryMetaVM,
  PublicLibraryVM,
  Template,
  TemplateHistoryItemStore,
  TemplateImport,
  TemplateListItemVM,
  TemplateStore,
  TemplateVM
} from "@cmodules/checklist/assets/data/types"
import { AnyItemVM } from "@cmodules/checklist/assets/data/types/vm/template-builder/item"
import { CHECKLIST_STORE_ACTION_TYPES } from "@cmodules/checklist/redux/reducers/actions"
import { PublicLibraryAPI } from "@cmodules/checklist/utility/public-library-api"
import {
  _depth,
  injectCalculationsRelations,
  injectDocumentIdRelations,
  injectRulesRelations
} from "@cmodules/checklist/utility/templateBuilderContext-new"
import { FieldsBehaviour } from "@csrc/assets/data/constants/FieldsBehaviour"
import { store } from '@csrc/redux/storeConfig/store'
import { _setGlobal, API } from "@utils"
import { ServerItemType } from '../../assets/data/types/vm/template-builder/server-item-type'
import { structureDataForPreview } from '../../utility/template-builder-utilities'


//******************  ******************//
const migrateSiteToAssetAndAssetField = (item: ItemVM<'site'>, items: AnyItemVM[], isAssetQuestionAdded: boolean) => {
  const assetTypes: AssetTypesResponse = _.get(store.getState(), 'asset.data.asset_types')
  const parentPage = _getParentPage(item, items)
  let finalItem: ItemVM<'asset'> | ItemVM<'asset_field'>
  if (parentPage && parentPage?.type === 'start_page' && !isAssetQuestionAdded && (item?.options?.default_value === "title" || !item?.options?.default_value)) {
    const convertedItem = ItemVM.cloneToType(item, 'asset')
    convertedItem.type = 'asset'
    isAssetQuestionAdded = true
    finalItem = convertedItem
  } else {
    const convertedItem = ItemVM.cloneToType(item, 'asset_field')
    convertedItem.options.asset_type_uuid = _.get(_.find(assetTypes, x => x.id === item?.options?.asset_type_id), 'uuid', undefined)
    if (item.options.field_type) {
      convertedItem.options.value_type = FieldsBehaviour[item.options.field_type].behaviouralRuleType
    }
    // item.options.asset_type_id = item?.options?.column_options?.structure_id
    // if (item?.options?.default_value === "title") {
    // 	item.options.field_key = "asset_id"
    // 	item.options.field_type = "text"
    // } else {
    // 	item.options.field_key = item.options?.column_options?.column_id `f_${item.options?.column_options?.column_id}`: null
    // }
    finalItem = convertedItem
  }
  return { isAssetQuestionAdded, item: finalItem }
}
//******************  ******************//
export const preProcessTemplateDataFromAPI = (data: Template): Template => {
  if (data.options.template_main_item_base_id) {
    const mainItemIndex = data.items.findIndex(i => i.base_id === data.options.template_main_item_base_id)
    if (mainItemIndex > -1) {
      data.items[mainItemIndex].options.repeatable = undefined
      data.items[mainItemIndex].options.minimum_repetition = undefined
      data.items[mainItemIndex].options.maximum_repetition = undefined
    }
    return data
  } else {
    return data
  }
}
//******************  ******************//
/**
 * Initializes template data from API response and converts it into a TemplateStore.
 * 
 * This function processes template data by:
 * - Converting items to ItemVM format with calculated depth
 * - Migrating site items to asset/asset_field items when needed
 * - Handling global items by setting their titles
 * - Injecting calculation and document ID relations
 * - Processing repeating groups: when the item is the child of repeating group, 
 *   prepare repeating group for being deleted by giving its parent_id and depth to its child
 * - Filtering out non-draggable repeating_group items from the final list
 * - Recursively processing global items
 * - Building rule relations from rule sets
 * 
 * @param data - The template data received from the API
 * @param depthOffset - Optional depth offset to add to calculated item depths (default: 0)
 * @returns A new TemplateStore instance containing the processed template data with past/present/future states and all related data structures
 */
export const initTemplateDataFromAPI = (data: Template, depthOffset = 0) => {
  let isAssetQuestionAdded = !_.isEmpty(_.find(data.items, x => x.type === 'asset'))
  let relations = {}
  const items: AnyItemVM[] = []
  const repeatableItems: AnyItemVM[] = []
  data.items.forEach(x => {
    let item: AnyItemVM = x
    if (x.id) {
      item.depth = _depth(x.id, data.items) + depthOffset
    }
    if (x.type === 'site') {
      const mapResponse = migrateSiteToAssetAndAssetField(x, data.items, isAssetQuestionAdded)
      isAssetQuestionAdded = mapResponse.isAssetQuestionAdded
      if (mapResponse.item.type === 'asset') {
        item = new ItemVM<'asset'>(mapResponse.item)
      } else {
        item = new ItemVM<'asset_field'>(mapResponse.item)
      }
      data.need_merge_request = true
    }
    // if (x.type === 'asset_field') {
    //   x.options.value_type = x.options.value_type ?? x.options.field_type
    // }
    if (x.type === 'global_item') {
      item.title = data?.flat_global_items?.[x?.id ?? '']?.title ?? ''
    }
    try {
      relations = injectCalculationsRelations(x, data.items, relations)
      relations = injectDocumentIdRelations(x, data.items, relations)
    } catch (e) {
      console.error(e)
    }
    /**
     * Prepare repeating_group for being deleted by giving its parent_id and depth to its child
     */
    if (x?.options?.repeatable && x.type !== 'repeating_group') {
      const repeating_group = _.find(repeatableItems, aa => aa.id === x.parent_id)
      item.parent_id = repeating_group?.parent_id ?? null
      item.depth = repeating_group?.depth ?? null
    }

    /**
     * Exclude repeating_group from items list as it's not draggable and not visible in the list
     */
    if (item.type !== 'repeating_group') {
      items.push(item)
    } else {
      repeatableItems.push(item)
    }

  })

  try {
    relations = injectRulesRelations(data, relations, data.rule_sets)
  } catch (e) {
    console.error(e)
  }

  data.items = items
  const response_sets = data.responses ?? {}
  const info = data.info ?? {}
  const image_markers = data.image_markers ?? {}
  const grouped_items = data.grouped_items ?? {}
  const global_responses = data.global_responses ?? {}
  const rule_sets = data.rule_sets ?? {}
  const nested_templates = data.global_items ?? []
  const translations = data.translations ?? []

  const global_items: Record<string, TemplateStore> = {}
  _.forEach(data.flat_global_items, (global_item: TemplateVM, global_item_key) => {
    const globalItemStore = initTemplateDataFromAPI(new Template({}, global_item))
    if (globalItemStore.present.options.template_type !== 'page' && globalItemStore.present.options.template_type !== 'start_page' && globalItemStore.present.options.template_type !== 'end_page') {
      globalItemStore.present.items = globalItemStore.present.items.filter(x => x.type !== 'page')
    }
    global_items[global_item_key] = globalItemStore
  })

  return new TemplateStore({
    past: [],
    present: data,
    future: [],
    response_sets,
    grouped_items,
    global_responses,
    info,
    image_markers,
    rule_sets,
    global_items,
    relations,
    nested_templates,
    translations
  })
}
//******************  ******************//
export function _setGlobalTemplateInfo(id: string, callback: (data: TemplateStore) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    API.get(`/checklist/global-items/${id}`)
      .then(async function (res: { data: TemplateVM }) {
        const resData = res.data

        const { subTemplates: global_items, nested_templates } = await iterateSubTemplates(_.cloneDeep(resData))

        const tempData: Template = new Template({ flat_global_items: global_items, global_items: nested_templates }, resData)
        // _logWithFileContext({ tempData, resData })
        const preProcessedData = preProcessTemplateDataFromAPI(tempData)
        const finalData = initTemplateDataFromAPI(preProcessedData)

        dispatch({ type: CHECKLIST_STORE_ACTION_TYPES.CLK_TEMPLATE_SET_INFO, payload: finalData })
        _setGlobal('ckl_revision', res.data.revision)
        callback(finalData)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export async function _getGlobalTemplateInfo(id: string, version: string): Promise<TemplateVM> {
  const res: { data: TemplateVM } = await API.get(`/checklist/global-items/${id}?version=${version ?? 'draft'}`)
  return res.data
}
//******************  ******************//
export async function _getTemplateInfo(id: string, version: string): Promise<TemplateVM> {
  const res: { data: TemplateVM } = await API.get(`/checklist/template/${id}?version=${version ?? 'latest'}`)
  return res.data
}
//******************  ******************//
// export async function getGlobalTemplateInfoForStorage(id: string) {
//   const resData = await _getGlobalTemplateInfo(id)

//   const tempData: Template = new Template({}, resData)
//   const globalItemStore = initTemplateDataFromAPI(tempData)
//   if (globalItemStore.present.options.template_type !== 'page' && globalItemStore.present.options.template_type !== 'start_page' && globalItemStore.present.options.template_type !== 'end_page') {
//     globalItemStore.present.items = globalItemStore.present.items.filter(x => x.type !== 'page')
//   }
//   return globalItemStore
// }
//******************  ******************//
export async function getNestedGlobalTemplateInfoForStorage(global_item_id: string, item_id: string, dive_deeper = true): Promise<{ globalItems: { [key: string]: TemplateStore }, nested_templates: Template[] }> {
  const resData = await _getGlobalTemplateInfo(global_item_id, "latest")

  let globalItems: { [key: string]: TemplateStore } = {}
  const nested_templates: Template[] = []

  let tempData: Template = new Template({}, resData)
  globalItems[item_id] = initTemplateDataFromAPI(tempData)
  if (globalItems[item_id].present.options.template_type !== 'page' && globalItems[item_id].present.options.template_type !== 'start_page' && globalItems[item_id].present.options.template_type !== 'end_page') {
    globalItems[item_id].present.items = globalItems[item_id].present.items.filter(x => x.type !== 'page')
    if (!dive_deeper) {
      tempData = structureDataForPreview(tempData)
      nested_templates.push(tempData)
    }
  }
  if (dive_deeper) {
    const nestedGlobalItems = globalItems[item_id].present.items.filter(x => x.options.global_item_id)
    if (nestedGlobalItems.length === 0) {
      tempData = structureDataForPreview(tempData)
      nested_templates.push(tempData)
    }
    await Promise.all(
      nestedGlobalItems.map(async (item) => {
        const result = await getNestedGlobalTemplateInfoForStorage(
          item?.options?.global_item_id ?? '',
          item.id,
          true
        )
        const { globalItems: nestedResults, nested_templates: newNestedTemplates } = result

        tempData = structureDataForPreview({ ...tempData, global_items: newNestedTemplates })
        nested_templates.push(tempData)

        globalItems = {
          ...globalItems,
          ...nestedResults
        }


        return result
      })
    )
  }
  return {
    globalItems,
    nested_templates
  }
}
//******************  ******************//
export function addNewSubTemplateToStore(subTemplate: TemplateStore, item_id: string) {
  return (dispatch: Dispatch) => {
    dispatch({ type: CHECKLIST_STORE_ACTION_TYPES.CLK_SUB_TEMPLATE_ADD_NEW_SUB_TEMPLATE, payload: { template: subTemplate, item_id } })
  }
}
//******************  ******************//
export function addNewMultipleSubTemplateToStore({ globalItems: subTemplates, nested_templates }: { globalItems: { [key: string]: TemplateStore }, nested_templates: Template[] }) {
  return (dispatch: Dispatch) => {
    dispatch({ type: CHECKLIST_STORE_ACTION_TYPES.CLK_SUB_TEMPLATE_ADD_NEW_MULTIPLE_SUB_TEMPLATE, payload: { templates: subTemplates, nested_templates } })
  }
}
//******************  ******************//
const iterateSubTemplates = async (templateData: TemplateVM) => {
  let subTemplates: Template['flat_global_items'] = {}
  const nested_templates: Template[] = []
  const subTemplateItems = templateData.items.filter(item => item.options.global_item_id)

  // Use Promise.all to wait for all async operations
  await Promise.all(
    subTemplateItems.map(async item => {
      if (item.options.global_item_id) {
        let newNestedTemplate: Template = {} as Template
        subTemplates[item.id] = await _getGlobalTemplateInfo(item.options.global_item_id, "latest")
        const currentSubTemplate = subTemplates[item.id]
        if (_.find(currentSubTemplate.items, x => x.options.global_item_id)) {
          const { subTemplates: subSubTemplates, nested_templates: nestedSubTemplates } = await iterateSubTemplates(currentSubTemplate)

          /**
           * Separate nested sub-templates into its own templates object to avoid re-generating same sub-template multiple times for preview data
           */
          const newSubTemplate = new Template({}, currentSubTemplate)
          newSubTemplate.global_items = [
            ...newSubTemplate.global_items,
            ...nestedSubTemplates
          ]
          newNestedTemplate = newSubTemplate

          subTemplates = {
            ...subTemplates,
            ...subSubTemplates
          }
        } else {
          newNestedTemplate = new Template({}, currentSubTemplate)
        }
        newNestedTemplate = structureDataForPreview(newNestedTemplate)
        nested_templates.push(newNestedTemplate)
      }
    })
  )
  return {
    subTemplates,
    nested_templates
  }
}
//******************  ******************//
export function _setTemplateInfo(id: string, callback: (data: TemplateStore) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    // const resData = require("./template.json")
    // const tempData: Template = new Template({ flat_global_items: {}, global_items: [] }, resData)
    // const data = initTemplateDataFromAPI(tempData)

    // dispatch({ type: CHECKLIST_STORE_ACTION_TYPES.CLK_TEMPLATE_SET_INFO, payload: data })
    // // _setGlobal('ckl_revision', res.data.revision)
    // callback(data)
    API.get(`/checklist/template/${id}`)
      .then(async function (res: { data: TemplateVM }) {
        let resData
        if (sessionStorage.getItem("temp_template_for_testing")) {
          // resData = JSON.parse(sessionStorage.getItem("temp_template_for_testing") ?? "") as TemplateVM
          resData = res.data
        } else {
          resData = res.data
        }
        const { subTemplates: global_items, nested_templates } = await iterateSubTemplates(resData)

        const tempData: Template = new Template({ flat_global_items: global_items, global_items: nested_templates }, resData)
        const data = initTemplateDataFromAPI(tempData)

        dispatch({ type: CHECKLIST_STORE_ACTION_TYPES.CLK_TEMPLATE_SET_INFO, payload: data })
        _setGlobal('ckl_revision', res.data.revision)
        callback(data)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export const _searchSubTemplate = async ({ q = '', types = ['page', 'section', 'item', 'custom'], mainItemType = null }: { q: string, types: GlobalItemType[], mainItemType: ServerItemType | null }) => {
  if (mainItemType) {
    if (mainItemType === 'section') types = ['section', 'custom']
    else if (mainItemType) types = ['item']
  }
  const res: { data: TemplateListItemVM[] } = await API.get('/checklist/global-items/search', {
    params: {
      // limit: 20,
      types,
      main_item_type: mainItemType,
      q
    }
  })
  return res.data
    .map(v => ({
      value: v.template_id,
      label: v.title ?? 'No title',
      data: v
    }))
}
//******************  ******************//
export function _getPublicLibraryCategories(with_templates = false, callback: (data: CategoryVM[]) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    PublicLibraryAPI().get(`/categories?with_templates=${with_templates}`)
      .then(function (res: { data: CategoryVM[] }) {
        callback(res.data)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export const editSubTemplate = (data: TemplateHistoryItemStore, callback = () => { }, sendEditRequest = true) => {
  return (dispatch: Dispatch) => {
    const injectedProps = {}
    // const time = moment.now()
    dispatch({ type: "CLK_SUB_TEMPLATE_EDIT", payload: { template: { ...data, ...injectedProps }, sendEditRequest } })
    // SendDropItemToFlutterApp(data.items)
    callback()
  }
}
//******************  ******************//
export function _getPublicLibrariesByCategory(categoryId: string, callback: (data: PublicLibraryMetaVM[]) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    PublicLibraryAPI().get(`/templates?category_id=${categoryId}`)
      .then(function (res: { data: PublicLibraryMetaVM[] }) {
        callback(res.data)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export function _getPublicLibraries(callback: (data: PublicLibraryMetaVM[]) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    PublicLibraryAPI().get(`/templates`)
      .then(function (res: { data: PublicLibraryMetaVM[] }) {
        callback(res.data)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export function _getPublicLibraryDetails(templateId: string, callback: (data: PublicLibraryVM) => void, callbackError: (res: any) => void) {
  return (dispatch: Dispatch) => {
    PublicLibraryAPI().get(`/templates/${templateId}`)
      .then(function (res: { data: PublicLibraryVM }) {
        callback(res.data)
      })
      .catch(callbackError)
  }
}
//******************  ******************//
export function _addTemplate(parent_id?: number, data?: TemplateImport, callback?: { (data: { id: string }): void }) {
  API.post('/checklist/template/new', {
    ...data,
    parent_id
  })
    .then(function ({ data }) {
      if (callback) {
        callback(data)
      }
    })
}
//******************  ******************//
export function _shareTemplate(data: { id: string, category_id: number, template_data: any }, callback?: { (): void }) {
  API.post(`/checklist/template/${data.id}/export-to-library`, {
    category_id: data.category_id,
    template_data: data.template_data
  })
    .then(function () {
      if (callback) {
        callback()
      }
    })
}
//******************  ******************//
export function _getGlobalTemplatesList({ limit, page, sort = [{ key: 'created_at', direction: 'desc' }], filter, withSavedQueries }: { limit: number, page: number, sort: any, filter: any, withSavedQueries: any }, callback: (data: TemplateListItemVM[]) => void, callbackError: (res: any) => void) {
  API.get('/checklist/global-items/list', {
    params: {
      limit,
      page,
      sort,
      filter,
      with_saved_queries: withSavedQueries
    }
  })
    .then(function ({ data }) {
      callback(data)
    })
    .catch(callbackError)
}
//******************  ******************//
export function _addGlobalTemplate(type: GlobalItemType, callback?: { (data: { id: string }): void }) {
  API.post('/checklist/global-items/new', {
    type
  })
    .then(function ({ data }) {
      if (callback) {
        callback(data)
      }
    })
}
//******************  ******************//
export function _addSingleItemGlobalTemplate({ type, mainItemType }: { type: GlobalItemType, mainItemType: ServerItemType }, callback?: { (data: { id: string }): void }) {
  API.post('/checklist/global-items/new', {
    type,
    main_item_type: mainItemType
  })
    .then(function ({ data }) {
      if (callback) {
        callback(data)
      }
    })
}
//******************  ******************//
export function _convertItemToGlobalItem(data: { item_id: string, template_id: string }, callback?: { (data: TemplateListItemVM): void }) {
  API.post('/checklist/global-items/create-from-template-item', data)
    .then(function ({ data }) {
      if (callback) {
        callback(data)
      }
    })
}